// ============================================================
// tokens.scss.ts
// Tokens de design système pour l'application Angular
// Toutes les couleurs, typographie, ombres, radius, espacements
// Pilotables dynamiquement via backend (COLOR_THEME)
// ============================================================

export const tokens = {
  // ═══════════════════════════════════════════════════════════
  // COULEURS NEUTRES (échelle Tailwind 50-950)
  // ═══════════════════════════════════════════════════════════
  colorWhite: '#ffffff',
  colorBlack: '#000000',

  // Neutres
  colorNeutral50: '#f8fafc',
  colorNeutral100: '#f1f5f9',
  colorNeutral200: '#e2e8f0',
  colorNeutral300: '#cbd5e1',
  colorNeutral400: '#94a3b8',
  colorNeutral500: '#64748b',
  colorNeutral600: '#475569',
  colorNeutral700: '#374151',
  colorNeutral800: '#1e293b',
  colorNeutral900: '#0f172a',
  colorNeutral950: '#0d1b2e',

  // Gris (alias)
  colorGray50: '#f8fafc',
  colorGray100: '#f1f5f9',
  colorGray200: '#e2e8f0',
  colorGray300: '#cbd5e1',
  colorGray400: '#94a3b8',
  colorGray500: '#64748b',
  colorGray600: '#475569',
  colorGray700: '#374151',
  colorGray800: '#1e293b',
  colorGray900: '#0f172a',

  // ═══════════════════════════════════════════════════════════
  // COULEURS PRIMAIRES & ACCENTS
  // ═══════════════════════════════════════════════════════════
  
  // Primaire (violet/bleu)
  colorPrimary: '#5b4fff',
  colorPrimary50: '#f0f4ff',
  colorPrimary100: '#e8edff',
  colorPrimary200: '#dde5ff',
  colorPrimary300: '#c7d7fd',
  colorPrimary400: '#bfdbfe',
  colorPrimary500: '#2563eb',
  colorPrimary600: '#1d4ed8',
  colorPrimary700: '#6d28d9',
  colorPrimary800: '#5b21b6',
  colorPrimaryDark: '#4a4e8c',
  colorPrimaryLight: 'rgba(91, 79, 255, 0.08)',
  colorPrimarySurface: 'rgba(91, 79, 255, 0.04)',
  colorPrimaryRing: '#bfdbfe',
  colorPrimarySoft: 'rgba(91, 79, 255, 0.07)',
  colorPrimaryHover: 'rgba(108, 99, 255, 0.06)',
  colorPrimaryLight2: '#eff6ff',

  // Accent (teal/cyan)
  colorAccent: '#06b6ac',
  colorAccentDark: '#0f766e',
  colorAccentLight: '#5eead4',
  colorAccentSurface: '#e6f6f5',
  colorAccentVariant: '#00c9a7',
  colorAccentVariantDark: '#00a888',
  colorAccentVariantLight: '#ccfbf1',
  colorAccentVariantSurface: '#f0fdfa',
  colorAccentTeal: '#0891b2',
  colorAccentTealDark: '#0d9488',
  colorAccentTealLight: '#ccf2eb',

  // Cyan
  colorCyan: '#06b6d4',
  colorCyanDark: '#0891b2',

  // Violet
  colorViolet: '#7c3aed',
  colorVioletLight: '#c4b5fd',
  colorVioletSurface: '#f5f3ff',
  colorVioletVariant: '#8b5cf6',

  // ═══════════════════════════════════════════════════════════
  // COULEURS D'ÉTAT
  // ═══════════════════════════════════════════════════════════

  // Succès (vert)
  colorSuccess: '#0ea55a',
  colorSuccess50: '#f0fdf4',
  colorSuccess100: '#d1fae5',
  colorSuccess200: '#bbf7d0',
  colorSuccess300: '#86efac',
  colorSuccess400: '#4ade80',
  colorSuccess500: '#22c55e',
  colorSuccess600: '#16a34a',
  colorSuccess700: '#15803d',
  colorSuccess800: '#14532d',
  colorSuccessDark: '#059669',
  colorSuccessLight: '#ccfbf1',
  colorSuccessSurface: '#f2f5f3',
  colorSuccessBg: '#f0fdf4',
  colorSuccessRing: '#bbf7d0',

  // Danger (rouge)
  colorDanger: '#dc2626',
  colorDanger50: '#fef2f2',
  colorDanger100: '#fee2e2',
  colorDanger200: '#fecaca',
  colorDanger300: '#fca5a5',
  colorDanger400: '#f87171',
  colorDanger500: '#ef4444',
  colorDanger600: '#dc2626',
  colorDanger700: '#b91c1c',
  colorDanger800: '#991b1b',
  colorDanger900: '#7f1d1d',
  colorDangerDark: '#b91c1c',
  colorDangerLight: '#f87171',
  colorDangerSurface: '#fef2f2',
  colorDangerRing: '#fecaca',
  colorDangerBg: '#fdf2f2',
  colorDangerSoft: '#fff6f6',

  // Warning (orange/ambre)
  colorWarning: '#d97706',
  colorWarning50: '#fffbeb',
  colorWarning100: '#fef3c7',
  colorWarning200: '#fde68a',
  colorWarning300: '#fcd34d',
  colorWarning400: '#fbbf24',
  colorWarning500: '#f59e0b',
  colorWarning600: '#d97706',
  colorWarning700: '#b45309',
  colorWarning800: '#92400e',
  colorWarning900: '#78350f',
  colorWarningLight: '#fef3c7',
  colorWarningSurface: '#fffbeb',
  colorWarningRing: '#fde68a',
  colorWarningBg: '#fff8f0',

  // Info (bleu ciel)
  colorInfo: '#0ea5e9',
  colorInfo50: '#f0f9ff',
  colorInfo100: '#e0f2fe',
  colorInfo200: '#bae6fd',
  colorInfo300: '#7dd3fc',
  colorInfo400: '#38bdf8',
  colorInfo500: '#0ea5e9',
  colorInfo600: '#0284c7',
  colorInfo700: '#0369a1',
  colorInfo800: '#075985',
  colorInfo900: '#0c4a6e',

  // ═══════════════════════════════════════════════════════════
  // COULEURS DE NIVEAU (pour structures programmatiques)
  // ═══════════════════════════════════════════════════════════

  colorLevel0: '#0f7cff',
  colorLevel0Bg: '#eef4ff',
  colorLevel0Border: '#bfdbfe',
  colorLevel0Text: '#1e40af',

  colorLevel1: '#f97316',
  colorLevel1Bg: '#fff7ed',
  colorLevel1Border: '#fed7aa',
  colorLevel1Text: '#9a3412',

  colorLevel2: '#16a34a',
  colorLevel2Bg: '#f0fdf4',
  colorLevel2Border: '#bbf7d0',
  colorLevel2Text: '#14532d',

  colorLevel3: '#8b5cf6',
  colorLevel3Bg: '#f5f3ff',
  colorLevel3Border: '#ddd6fe',
  colorLevel3Text: '#4c1d95',

  colorLevel4: '#0d9488',
  colorLevel4Bg: '#f0fdfa',
  colorLevel4Border: '#99f6e4',
  colorLevel4Text: '#0f5e57',

  colorLevel0Hover: '#0b66d1',
  colorLevel1Dark: '#c2500a',

  // ═══════════════════════════════════════════════════════════
  // SURFACES & FONDS
  // ═══════════════════════════════════════════════════════════

  colorSurface: '#ffffff',
  colorSurfaceAlt: '#f8f7f5',
  colorSurfaceAlt2: '#f7f7fb',
  colorSurfaceAlt3: '#f3f6fc',
  colorSurfaceDark: '#f9f8f4',
  colorSurfaceCard: '#fafbfc',
  colorSurfaceBg: '#f4f4f8',
  colorSurfaceInput: '#f7f9fc',
  colorSurfaceHover: '#f4f4f5',
  colorSurfaceHover2: '#f4f6f8',
  colorSurfaceActive: '#f0fdf8',
  colorSurfaceSkeleton: '#e8ecf0',
  colorSurfaceTeal: '#f0fdfa',
  colorSurfaceTeal2: '#e6f6f5',
  colorSurfaceViolet: '#f0f4ff',
  colorSurfaceError: '#fff7f7',
  colorSurfaceSuccess: '#f2f5f3',
  colorSurfaceWarning: '#fffbeb',
  colorSurfaceInfo: '#f0f9ff',
  colorBgPage: '#f1f5f9',
  colorBgAlt: '#fafbfc',
  colorBgCard: '#ffffff',
  colorBgCardTransparent: 'rgba(255,255,255,0.02)',
  colorBgHeader: '#fafbfc',
  colorBgTableEven: '#f3f3f3',
  colorBgTableHover: 'rgba(91, 79, 255, 0.025)',
  colorBgSection: '#f8f8fc',
  colorBgHover: 'rgba(255,255,255,0.02)',
  colorSurfaceBlueLight: '#f4f7ff',
  colorSurfaceBlueLight2: '#eef1fa',
  colorSurfaceBlueSubtle: '#fafbff',
  colorSurfaceMenuActive: '#eff2ff',

  // ═══════════════════════════════════════════════════════════
  // COULEURS DE TEXTE
  // ═══════════════════════════════════════════════════════════

  colorTextPrimary: '#0e0e12',
  colorTextSecondary: '#4a4a5a',
  colorTextMuted: '#8e8ea8',
  colorTextBody: '#5a6880',
  colorTextLight: '#8a95a8',
  colorTextBlack: '#000000',
  colorTextTitle: '#1a1a2e',
  colorTextSubtitle: '#6b7280',
  colorTextDark: '#0f1724',
  colorTextOnPrimary: '#ffffff',
  colorTextOnDanger: '#ffffff',
  colorTextOnSuccess: '#ffffff',
  colorTextOnWarning: '#ffffff',
  colorText900: '#111827',
  colorEmerald500: '#10b981',
  colorRose400: '#fb7185',
  colorTeal500: '#06b6a4',
  colorTextDarkStrong: '#1e1e1e',
  colorTextMutedIcon: '#9ca3af',
  colorTextPlaceholder: '#a0aab8',
  colorTextPlaceholderLight: '#c2c9d6',
  colorTextHint: '#b0b8c9',
  colorTextMutedItalic: '#aab3c2',
  colorIconMuted: '#a0aec0',

  // ═══════════════════════════════════════════════════════════
  // COULEURS DE BORDURE
  // ═══════════════════════════════════════════════════════════

  colorBorder: '#e2e8f0',
  colorBorderLight: '#f0f0f5',
  colorBorderMedium: '#d0d0e0',
  colorBorderDark: '#b0bac6',
  colorBorderStrong: '#e8e8f0',
  colorBorderInput: '#d1d5db',
  colorBorderInput2: '#d1d9e6',
  colorBorderFocus: '#7c3aed',
  colorBorderCard: '#e3e8f0',
  colorBorderDivider: '#f0f2f7',
  colorBorderTable: '#dddddd',
  colorBorderSuccess: '#009879',
  colorBorderError: '#fecaca',
  colorBorderWarning: '#fde68a',
  colorBorderInfo: '#bae6fd',
  colorBorderTransparent: 'rgba(15, 23, 42, .08)',
  colorBorderCardTransparent: 'rgba(255,255,255,0.04)',
  colorBorderSoft: '#dde3ef',
  colorBorderHover: '#c8d0df',
  colorBorderDropdown: '#e5e7eb',
  colorBorderDashed: '#c8d6ef',

  // ═══════════════════════════════════════════════════════════
  // OMBRES
  // ═══════════════════════════════════════════════════════════

  shadowXs: '0 1px 0 rgba(0, 0, 0, .04)',
  shadowSm: '0 1px 3px rgba(15, 23, 42, 0.05)',
  shadowMd: '0 1px 3px rgba(15, 23, 42, 0.06)',
  shadowLg: '0 8px 32px rgba(15, 23, 42, 0.08)',
  shadowXl: '0 20px 48px rgba(0, 0, 0, 0.12)',
  shadow2Xl: '0 12px 40px rgba(11, 24, 50, 0.14)',
  shadow3Xl: '0 20px 60px rgba(0,0,0,.18)',

  shadowCard: '0 2px 12px rgba(14, 14, 18, 0.08)',
  shadowCard2: '0 1px 3px rgba(0,0,0,.06)',
  shadowCard3: '0 4px 20px rgba(0,0,0,.05)',
  shadowDropdown: '0 12px 40px rgba(14, 14, 18, 0.10)',
  shadowDropdown2: '0 4px 16px rgba(11, 24, 50, 0.12)',
  shadowInput: '0 1px 3px rgba(14, 14, 18, 0.06)',
  shadowInputFocus: '0 0 0 3px rgba(15, 124, 255, 0.12)',
  shadowSidebar: '2px 0 24px rgba(0,0,0,.07)',
  shadowModal: '0 4px 6px rgba(11, 24, 50, 0.04)',
  shadowPrimary: '0 8px 24px rgba(108, 99, 255, 0.12)',
  shadowPrimary2: '0 4px 14px rgba(220,38,38,.35)',
  shadowAccent: '0 4px 12px rgba(6, 182, 172, 0.35)',
  shadowTeal: '0 4px 12px rgba(13, 148, 136, 0.25)',
  shadowHover: '0 8px 20px rgba(2, 6, 23, 0.06)',
  shadowBtn: '0 1px 4px rgba(6, 182, 172, 0.25)',
  shadowStep: '0 0 0 3px #bfdbfe',

  // ═══════════════════════════════════════════════════════════
  // TYPOGRAPHIE
  // ═══════════════════════════════════════════════════════════

  fontFamily: "'DM Sans', system-ui, sans-serif",
  fontFamilyAlt: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto",
  fontFamilyUi: "'Outfit', 'Segoe UI', sans-serif",

  fontSizeXs: '0.65rem',
  fontSizeSm: '0.72rem',
  fontSizeBase: '0.82rem',
  fontSizeMd: '0.845rem',
  fontSizeLg: '0.92rem',
  fontSizeXl: '15px',
  fontSize2Xl: '1rem',
  fontSize3Xl: '1.125rem',
  fontSize4Xl: '1.25rem',
  fontSize5Xl: '1.35rem',
  fontSize6Xl: '1.375rem',
  fontSize7Xl: '1.5rem',
  fontSize8Xl: '1.6rem',
  fontSize9Xl: '1.8rem',
  fontSize10Xl: '3.125rem',

  fontWeightNormal: '400',
  fontWeightMedium: '500',
  fontWeightSemibold: '600',
  fontWeightBold: '700',
  fontWeightExtraBold: '800',

  // ═══════════════════════════════════════════════════════════
  // RADIUS
  // ═══════════════════════════════════════════════════════════

  radiusXs: '4px',
  radiusSm: '6px',
  radiusMd: '8px',
  radiusLg: '10px',
  radiusXl: '12px',
  radius2Xl: '14px',
  radius3Xl: '16px',
  radius4Xl: '18px',
  radius5Xl: '20px',
  radiusFull: '9999px',

  // ═══════════════════════════════════════════════════════════
  // ESPACEMENTS
  // ═══════════════════════════════════════════════════════════

  spacing1: '4px',
  spacing2: '8px',
  spacing3: '12px',
  spacing4: '16px',
  spacing5: '20px',
  spacing6: '24px',
  spacing7: '28px',
  spacing8: '32px',
  spacing9: '36px',
  spacing10: '40px',
  spacing11: '44px',
  spacing12: '48px',
  spacing13: '56px',
  spacing14: '60px',
  spacing15: '64px',
  spacing16: '72px',

  // ═══════════════════════════════════════════════════════════
  // TRANSITIONS
  // ═══════════════════════════════════════════════════════════

  transitionFast: '0.12s ease',
  transitionBase: '0.18s ease',
  transitionSlow: '0.22s cubic-bezier(0.4, 0, 0.2, 1)',
  transitionBounce: '0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
  transitionSmooth: '0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  transitionBtn: '0.2s cubic-bezier(0.4, 0, 0.2, 1)',

  // ═══════════════════════════════════════════════════════════
  // VARIABLES GLOBALES (styles.css)
  // ═══════════════════════════════════════════════════════════

  appTextColor: '#000000',
  appCardBg: '#fefefe',
  appCardTextColor: '#000000',
  appHeaderBg: '#F7F7F9',
  btnSelectedBg: '#06b6ac',
  btnSelectedColor: '#ffffff',
  btnHoverTranslate: '-2px',
  btnHoverShadow: '0 8px 20px rgba(2, 6, 23, 0.06)',

  // ═══════════════════════════════════════════════════════════
  // VARIABLES DE SIDEBAR
  // ═══════════════════════════════════════════════════════════

  sbWidth: '240px',
  sbCollapsed: '68px',
  sbHeaderHeight: '68px',
  sbBg: '#ffffff',
  sbBorder: 'rgba(15,23,42,.08)',
  sbOverlay: 'rgba(15,23,42,.45)',
  sbText: '#000000',
  sbTextSub: '#1a1a1a',
  sbTextMuted: '#6b7280',
  sbIcon: '#000000',
  sbIconSub: '#111111',
  sbHoverBg: '#f3f4f6',
  sbHoverText: '#000000',
  sbActiveBg: '#f0fdf8',
  sbActiveBar: '#0ea5a0',
  sbActiveIcon: '#0ea5a0',
  sbActiveText: '#0a7a76',
  sbBadgeBg: '#0ea5a0',
  sbBadgeSoftBg: '#e6f9f8',
  sbBadgeSoftFg: '#0a7a76',
  sbRadius: '10px',
  sbShadow: '2px 0 24px rgba(0,0,0,.07)',
} as const;

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

export type TokenKey = keyof typeof tokens;
export type TokenValue = typeof tokens[TokenKey];

// ═══════════════════════════════════════════════════════════════
// MAPPING VERS NOMS DE VARIABLES CSS
// ═══════════════════════════════════════════════════════════════

export const tokenToCssVar = (tokenKey: TokenKey): string => {
  return `--${tokenKey.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
};

export const cssVarMap: Record<TokenKey, string> = {} as Record<TokenKey, string>;
for (const key of Object.keys(tokens) as TokenKey[]) {
  cssVarMap[key] = tokenToCssVar(key);
}

// ═══════════════════════════════════════════════════════════════
// EXPORT POUR GÉNÉRATION DES CSS VARIABLES
// ═══════════════════════════════════════════════════════════════

export const generateCssVariables = (): string => {
  let css = ':root {\n';
  for (const [key, value] of Object.entries(tokens)) {
    const varName = tokenToCssVar(key as TokenKey);
    css += `  ${varName}: ${value};\n`;
  }
  css += '}\n';
  return css;
};

export default tokens;